import { ref, reactive, computed } from 'vue';
import { hashData, rowTimestamp } from '@/domain/sync/DataSync.js';
import { tables, events } from '@/livestore/schema.js';
import { GoogleDriveProvider } from './providers/GoogleDriveProvider.js';
import { OneDriveProvider } from './providers/OneDriveProvider.js';
import { useSheetSync } from './useSheetSync.js';

const SYNC_FILE = 'punchlog-data.json';
const POLL_MS = 5 * 60_000;
const PROVIDERS_KEY = 'sync_providers';
const LEGACY_KEY = 'sync_provider'; // pre-multi-provider single-value key — migrated once

// Tables mirrored in the remote snapshot.
const SYNC_TABLES = [
  {
    evtInsert: events.timeEntryCreated,
    evtUpdate: events.timeEntryUpdated,
    name: 'timeEntries',
    pk: 'id',
    tbl: tables.timeEntries,
  },
  {
    evtInsert: events.workplaceCreated,
    evtUpdate: events.workplaceUpdated,
    name: 'workplaces',
    pk: 'id',
    tbl: tables.workplaces,
  },
  {
    evtInsert: events.scheduleTemplateCreated,
    evtUpdate: events.scheduleTemplateUpdated,
    name: 'scheduleTemplates',
    pk: 'id',
    tbl: tables.scheduleTemplates,
  },
  {
    evtInsert: events.signedBundleStored,
    evtUpdate: null,
    name: 'signedBundles',
    pk: 'bundle_id',
    tbl: tables.signedBundles,
  },
] as const;

// ── Provider registry (extensible) ─────────────────────────────────────────────
const PROVIDER_REGISTRY = {};

export function registerProvider(name, factory) {
  PROVIDER_REGISTRY[name] = factory;
}

registerProvider('google', () => new GoogleDriveProvider());
registerProvider('onedrive', () => new OneDriveProvider());

interface ProviderState {
  isSyncing: boolean;
  lastSyncAt: string | null;
  needsReauth: boolean;
  syncError: string | null;
}

function freshProviderState(): ProviderState {
  return { isSyncing: false, lastSyncAt: null, needsReauth: false, syncError: null };
}

function loadConnectedProviders(): string[] {
  const raw = localStorage.getItem(PROVIDERS_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* fall through to legacy/empty */
    }
  }
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (legacy) {
    localStorage.setItem(PROVIDERS_KEY, JSON.stringify([legacy]));
    localStorage.removeItem(LEGACY_KEY);
    return [legacy];
  }
  return [];
}

function persistConnectedProviders(names: string[]) {
  localStorage.setItem(PROVIDERS_KEY, JSON.stringify(names));
}

// Multiple providers can be connected at once — each gets its own instance, sync status,
// and last-sync-hash bookkeeping so one provider's failure/lag never affects another's.
export const connectedProviders = ref<string[]>(loadConnectedProviders());
export const providerState = reactive<Record<string, ProviderState>>(
  Object.fromEntries(connectedProviders.value.map(name => [name, freshProviderState()])),
);
export const syncImported = ref(0);

// Aggregate "app-wide last synced" — the most recent successful sync across every connected
// provider. App.vue's top-bar display doesn't need per-provider granularity.
export const lastSyncAt = computed<string | null>(() => {
  const times = connectedProviders.value
    .map(name => providerState[name]?.lastSyncAt)
    .filter((t): t is string => !!t);
  return times.length
    ? new Date(Math.max(...times.map(t => new Date(t).getTime()))).toISOString()
    : null;
});

let _providers: Record<string, any> = {};
let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _store: any = null; // set by initDriveSync(store)

export { hashData };

function getStore() {
  if (!_store) throw new Error('initDriveSync: store not initialised');
  return _store;
}

function bindProvider(name: string) {
  const factory = PROVIDER_REGISTRY[name];
  if (factory) _providers[name] = factory();
}

function unbindProvider(name: string) {
  delete _providers[name];
}

function exportData() {
  const s = getStore();
  const snap: Record<string, any[]> = {};
  for (const { name, tbl } of SYNC_TABLES) {
    snap[name] = s.query((tbl as any).select());
  }
  // version is the app version at export time (frontend-app/package.json, via __APP_VERSION__),
  // for future use only -- not acted on by mergeData()/mergeTable() yet. No schema-locking or
  // rejection of old-version backups; this is still deep in development.
  return { data: snap, exportedAt: new Date().toISOString(), version: __APP_VERSION__ };
}

function mergeTable({ evtInsert, evtUpdate, name, pk, tbl }, rows: any[]) {
  const s = getStore();
  let imported = false;
  for (const row of rows) {
    if (!row[pk]) continue;
    const existing = s.query(tbl.select().where({ [pk]: row[pk] }))[0];
    if (!existing) {
      s.commit(evtInsert(row));
      imported = true;
    } else if (rowTimestamp(row) > rowTimestamp(existing) && evtUpdate) {
      const fields = { ...row };
      delete fields[pk];
      // A never-edited remote row carries modified_timestamp/is_edited/updated_at as null
      // (set that way on create), but the *Updated event schemas require them as non-nullable
      // numbers -- applying the remote row as a local edit must set these the same way any
      // other edit path does, or every sync throws "Invalid event args".
      const now = Date.now();
      s.commit(
        evtUpdate({
          id: existing.id ?? existing[pk],
          ...fields,
          is_edited: fields.is_edited ?? 1,
          modified_timestamp: fields.modified_timestamp ?? now,
          updated_at: fields.updated_at ?? now,
        }),
      );
      imported = true;
    }
  }
  return imported;
}

function mergeData(remote) {
  if (!remote?.data) return false;
  let imported = false;
  for (const meta of SYNC_TABLES) {
    if (mergeTable(meta, remote.data[meta.name] ?? [])) imported = true;
  }
  return imported;
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Syncs one connected provider. Internal — external callers use sync(). */
async function syncOne(name: string) {
  const inst = _providers[name];
  const state = providerState[name];
  if (!inst || !state || state.isSyncing) return;

  state.isSyncing = true;
  state.syncError = null;
  const hashKey = `sync_last_hash_${name}`;
  try {
    const remote = await inst.downloadData(SYNC_FILE);
    const imported = mergeData(remote);
    const merged = exportData();

    const hash = await hashData(merged.data);
    if (hash !== localStorage.getItem(hashKey)) {
      await inst.uploadData(SYNC_FILE, merged);
      localStorage.setItem(hashKey, hash);
    }

    state.lastSyncAt = merged.exportedAt;
    localStorage.setItem(`sync_last_at_${name}`, state.lastSyncAt);
    state.needsReauth = false;
    if (imported) syncImported.value++;

    // Sheets import is a Google-only feature (fetchSheetValues lives on GoogleDriveProvider).
    if (name === 'google') {
      const { pullAll } = useSheetSync();
      const sheetResult = await pullAll().catch(() => ({ imported: 0 }));
      if (sheetResult.imported) syncImported.value++;
    }
  } catch (e) {
    if (e.message === 'token_expired') {
      state.needsReauth = true;
      return;
    }
    state.syncError = e.message;
    throw e;
  } finally {
    state.isSyncing = false;
  }
}

/** Syncs a specific connected provider, or every connected provider when called with no name. */
export async function sync(name?: string) {
  const names = name ? [name] : [...connectedProviders.value];
  const results = await Promise.allSettled(names.map(syncOne));
  const rejection = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (rejection) throw rejection.reason;
}

export async function connectProvider(
  name: string,
  opts: { overrideInteractionInProgress?: boolean } = {},
) {
  if (!PROVIDER_REGISTRY[name]) throw new Error(`Unknown sync provider: ${name}`);
  bindProvider(name);
  try {
    await _providers[name].signIn(opts);
  } catch (e) {
    unbindProvider(name);
    throw e;
  }
  if (!connectedProviders.value.includes(name)) {
    connectedProviders.value = [...connectedProviders.value, name];
  }
  providerState[name] = freshProviderState();
  persistConnectedProviders(connectedProviders.value);
  await sync(name).catch(() => {});
  startPolling();
}

export async function disconnectProvider(name: string) {
  const inst = _providers[name];
  if (inst) await inst.signOut().catch(() => {});
  unbindProvider(name);
  delete providerState[name];
  connectedProviders.value = connectedProviders.value.filter(p => p !== name);
  persistConnectedProviders(connectedProviders.value);
  localStorage.removeItem(`sync_last_at_${name}`);
  localStorage.removeItem(`sync_last_hash_${name}`);
  if (connectedProviders.value.length === 0) stopPolling();
}

export function startPolling() {
  stopPolling();
  if (connectedProviders.value.length === 0) return;
  _pollTimer = setInterval(() => sync().catch(() => {}), POLL_MS);
}

export function stopPolling() {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
}

// Test-only seam: resets all module-level state to initial values (use in beforeEach/afterEach).
export function _resetForTests() {
  stopPolling();
  _providers = {};
  _store = null;
  connectedProviders.value = loadConnectedProviders();
  for (const key of Object.keys(providerState)) delete providerState[key];
  for (const name of connectedProviders.value) providerState[name] = freshProviderState();
  syncImported.value = 0;
}

export function initDriveSync(store: any) {
  _store = store;
  for (const name of connectedProviders.value) bindProvider(name);
  if (connectedProviders.value.length > 0) startPolling();
}

export function useDriveSync() {
  return {
    connectedProviders,
    connectProvider,
    disconnectProvider,
    providerState,
    sync,
    syncImported,
  };
}
